import React, { useState, useEffect, useRef } from "react"
import { useNavigate, Link } from "react-router-dom"
import {
  MagnifyingGlass,
  CaretDown,
  BookmarkSimple,
  X,
  Gear,
  BookOpenText,
  Question,
  SignOut,
  User,
  Moon,
  Sun,
  List,
  ChatCircle,
  Robot,
  At,
  Ticket,
} from "@phosphor-icons/react"
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar"
import { SystemTicketDrawer } from "../../features/system-tickets"
import { useAuthStore, useUIStore } from "../../stores"
import { useChatStore } from "../../stores/chat-store"
import { DivisionSelector } from "./DivisionSelector"
import { SearchResults } from "./SearchResults"
import { QuickActionMenu } from "./QuickActionMenu"
import { PhoneStatusIndicator } from "./PhoneStatusIndicator"
import { NotificationButton } from "./NotificationButton"
import { ChangelogButton } from "../../features/changelog/components/ChangelogButton"
import { HeaderTab } from "./HeaderTab"
import { cn } from "../../lib/utils"
import { getLogoutUrl } from "../../lib/auth"
import { CreateContactModal } from "../../features/contacts/components/CreateContactModal"
import { CreateMatterModal } from "../../features/matters/components/CreateMatterModal"
import { GlobalAIDrawer, useGlobalAIStore } from "../../features/global-ai"
import { useQuery } from "@tanstack/react-query"
import { api } from "../../lib/api/client"
import { mentionsKeys } from "../../lib/query-keys"

// Secondary navigation tabs
const secondaryTabs = [
  { label: "Overview", href: "/overview" },
  { label: "Calendar", href: "/calendar" },
]

// Simple debounce utility
function debounce<T extends (...args: any[]) => void>(fn: T, delay: number): T {
  let timeoutId: ReturnType<typeof setTimeout>
  return ((...args: Parameters<T>) => {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => fn(...args), delay)
  }) as T
}

export function Header() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const {
    globalSearchQuery,
    setGlobalSearchQuery,
    setSearchResultsOpen,
    setMobileMenuOpen,
    theme,
    setTheme,
    selectedDivision,
  } = useUIStore()

  const { openChat, unreadCount, hasMentions } = useChatStore()

  const { data: mentionsCount = 0 } = useQuery({
    queryKey: mentionsKeys.count(selectedDivision || ""),
    queryFn: () => api.userComments.getCount(selectedDivision || ""),
    enabled: !!selectedDivision,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    select: (response) => (response as any)?.data ?? 0,
  })

  const [searchFocused, setSearchFocused] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [moreMenuOpen, setMoreMenuOpen] = useState(false)
  const [localSearchValue, setLocalSearchValue] = useState(globalSearchQuery)
  const [createContactOpen, setCreateContactOpen] = useState(false)
  const [createMatterOpen, setCreateMatterOpen] = useState(false)
  const [ticketDrawerOpen, setTicketDrawerOpen] = useState(false)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const userMenuRef = useRef<HTMLDivElement>(null)
  const moreMenuRef = useRef<HTMLDivElement>(null)

  // Debounced search - update global query after user stops typing
  const debouncedSetQuery = useRef(
    debounce((value: string) => {
      setGlobalSearchQuery(value)
    }, 300)
  ).current

  // Sync local value with global on external changes
  useEffect(() => {
    setLocalSearchValue(globalSearchQuery)
  }, [globalSearchQuery])

  // Close menus on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false)
      }
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target as Node)) {
        setMoreMenuOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  // Keyboard shortcut for search
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && searchFocused) {
        searchInputRef.current?.blur()
        setSearchResultsOpen(false)
      }
    }

    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [searchFocused, setSearchResultsOpen])

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setLocalSearchValue(value)
    debouncedSetQuery(value)
  }

  const handleSearchFocus = () => {
    setSearchFocused(true)
    setSearchResultsOpen(true)
  }

  const handleSearchBlur = () => {
    setSearchFocused(false)
  }

  const handleClearSearch = () => {
    setLocalSearchValue("")
    setGlobalSearchQuery("")
    setSearchResultsOpen(false)
    searchInputRef.current?.focus()
  }

  const handleLogout = () => {
    setUserMenuOpen(false)
    // Redirect first - don't call clearAuth() before redirect
    // as it triggers auth checks that redirect to login instead
    window.location.href = getLogoutUrl()
  }

  const handleProfileClick = () => {
    setUserMenuOpen(false)
    navigate("/settings/profile")
  }

  const handleTrainingClick = () => {
    setUserMenuOpen(false)
    navigate("/training")
  }

  const handleSettingsClick = () => {
    setUserMenuOpen(false)
    navigate("/settings")
  }

  const initials = user
    ? `${user.given_name?.[0] || ""}${user.family_name?.[0] || ""}`
    : "U"

  const displayName = user
    ? `${user.given_name || ""} ${user.family_name || ""}`.trim()
    : "User"

  const isOnline = true // TODO: Wire to actual online status

  return (
    <div
      className={cn(
        "relative flex flex-col",
        searchFocused ? "z-[100]" : "z-50",
        "overlay-backdrop bg-background/95 backdrop-blur-sm",
        "border-b border-border/80",
        "shadow-[0_1px_3px_0_rgba(0,0,0,0.02)]"
      )}
    >
      {/* Main header row */}
      <header className="flex h-14 items-center justify-between px-4 gap-2 lg:gap-4">
        {/* Mobile/Tablet: Hamburger menu */}
        <button
          className="flex items-center justify-center h-10 w-10 -ml-2 lg:hidden rounded-lg hover:bg-accent transition-colors"
          onClick={() => setMobileMenuOpen(true)}
          aria-label="Open menu"
        >
          <List className="h-5 w-5 text-muted-foreground" />
        </button>

        {/* Search - hidden on mobile/tablet */}
        <div className="hidden lg:flex items-center flex-1 max-w-lg">
          <div
            data-search-container
            className={cn(
              "relative flex-1 transition-all duration-200 ease-out",
              searchFocused ? "flex-[1.15] scale-[1.01]" : "scale-100"
            )}
          >
            <MagnifyingGlass
              className={cn(
                "absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transition-colors duration-200",
                searchFocused ? "text-primary" : "text-muted-foreground"
              )}
            />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search matters, contacts..."
              value={localSearchValue}
              onChange={handleSearchChange}
              onFocus={handleSearchFocus}
              onBlur={handleSearchBlur}
              className={cn(
                "w-full h-9 pl-10 pr-16 text-sm text-foreground",
                "bg-muted border border-border rounded-lg",
                "placeholder:text-muted-foreground",
                "transition-all duration-200 ease-out",
                "hover:bg-background hover:border-border hover:shadow-sm",
                "focus:outline-none focus:bg-background focus:border-primary",
                "focus:ring-2 focus:ring-primary/20 focus:shadow-md"
              )}
            />
            {/* Clear button */}
            {localSearchValue && (
              <button
                onClick={handleClearSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}

            {/* Search results dropdown */}
            <SearchResults />
          </div>
        </div>

        {/* Right side - Actions, Division selector, User menu */}
        <div className="flex items-center gap-1 lg:gap-2 flex-1 lg:flex-none justify-end">
          {/* Bookmark */}
          <Link
            to="/matters/bookmarked"
            className={cn(
              "flex items-center justify-center h-8 w-8 rounded-md",
              "text-primary hover:text-primary hover:bg-primary/10",
              "transition-colors"
            )}
            title="View bookmarked matters"
          >
            <BookmarkSimple className="h-4 w-4" />
          </Link>

          {/* Phone status */}
          <PhoneStatusIndicator />

          <ChangelogButton />

          <NotificationButton />

          {/* Divider */}
          <div className="hidden lg:block h-6 w-px bg-border mx-1" />

          {/* Division selector */}
          <DivisionSelector />

          {/* User menu */}
          <div ref={userMenuRef} className="relative">
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className={cn(
                "flex items-center gap-2.5 rounded-lg px-2 py-1.5",
                "transition-all duration-200",
                userMenuOpen ? "bg-accent shadow-inner" : "hover:bg-muted"
              )}
            >
              {/* Avatar with online status ring */}
              <div className="relative">
                <Avatar
                  className={cn(
                    "h-8 w-8 ring-2 ring-offset-1 transition-all duration-200",
                    isOnline ? "ring-success" : "ring-border"
                  )}
                >
                  <AvatarImage src={user?.avatar} />
                  <AvatarFallback className="text-sm bg-primary text-white font-semibold">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                {/* Status indicator */}
                <span
                  className={cn(
                    "absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-background",
                    isOnline ? "bg-success shadow-sm shadow-success/50" : "bg-muted-foreground"
                  )}
                />
              </div>
              <span className="hidden text-sm font-medium text-foreground lg:inline">
                {displayName}
              </span>
              <CaretDown
                className={cn(
                  "h-3 w-3 text-muted-foreground hidden lg:block transition-transform duration-200",
                  userMenuOpen && "rotate-180"
                )}
              />
            </button>

            {/* User dropdown menu */}
            {userMenuOpen && (
              <div
                className={cn(
                  "absolute right-0 top-full mt-2 w-64 rounded-xl",
                  "bg-background border border-border shadow-xl",
                  "overflow-hidden z-50",
                  "animate-flyout-enter"
                )}
              >
                {/* Header section */}
                <div className="px-4 py-3 bg-gradient-to-br from-muted to-background border-b border-border">
                  <p className="text-sm font-semibold text-foreground">{displayName}</p>
                  <p className="text-xs text-muted-foreground">{user?.email || "user@example.com"}</p>
                  <div
                    className={cn(
                      "flex items-center gap-1.5 mt-2 text-xs",
                      isOnline ? "text-success" : "text-muted-foreground"
                    )}
                  >
                    <span
                      className={cn(
                        "h-2 w-2 rounded-full",
                        isOnline ? "bg-success" : "bg-muted-foreground"
                      )}
                    />
                    <span>{isOnline ? "Online" : "Offline"}</span>
                  </div>
                </div>

                {/* Menu items */}
                <div className="py-1">
                 
                  <button
                    onClick={handleTrainingClick}
                    className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
                  >
                    <BookOpenText className="h-4 w-4 text-muted-foreground" />
                    <span>My Training</span>
                  </button>
                  <button
                    onClick={handleSettingsClick}
                    className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
                  >
                    <Gear className="h-4 w-4 text-muted-foreground" />
                    <span>Settings</span>
                    <span className="ml-auto text-xs text-muted-foreground font-mono bg-muted px-1.5 py-0.5 rounded">
                      ,
                    </span>
                  </button>
                  <button
                    onClick={() => {
                      const newTheme = theme === "light" ? "dark" : "light"
                      setTheme(newTheme)
                    }}
                    className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
                  >
                    {theme === "light" ? (
                      <Moon className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Sun className="h-4 w-4 text-yellow-400" />
                    )}
                    <span>Theme</span>
                    <span className="ml-auto text-xs text-muted-foreground capitalize">{theme}</span>
                  </button>
                </div>

                <div className="border-t border-border py-1">
                  <Link
                    to="/tickets"
                    onClick={() => setUserMenuOpen(false)}
                    className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
                  >
                    <Ticket className="h-4 w-4 text-muted-foreground" />
                    <span>My Tickets</span>
                  </Link>
                  <button
                    onClick={() => {
                      setUserMenuOpen(false)
                      setTicketDrawerOpen(true)
                    }}
                    className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
                  >
                    <Question className="h-4 w-4 text-muted-foreground" />
                    <span>Help & Support</span>
                  </button>
                </div>

                <div className="border-t border-border py-1">
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-danger hover:bg-danger/5 transition-colors"
                  >
                    <SignOut className="h-4 w-4" />
                    <span>Sign Out</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Secondary navigation tabs */}
      <div className="flex h-12 items-center justify-between px-2 lg:px-4 border-t border-border/50">
        {/* Left side - tabs and menus */}
        <div className="flex items-center">
          {/* Tabs (scrollable on mobile/tablet) */}
          <nav className="flex items-center overflow-x-auto scrollbar-hide">
            {secondaryTabs.map((tab) => (
              <HeaderTab key={tab.href} to={tab.href}>
                {tab.label}
              </HeaderTab>
            ))}
          </nav>

          {/* New action menu - hidden on mobile/tablet */}
          <div className="hidden lg:block">
            <QuickActionMenu
              onNewMatter={() => setCreateMatterOpen(true)}
              onNewContact={() => setCreateContactOpen(true)}
            />
          </div>

          {/* More menu - hidden on mobile/tablet, outside nav to prevent overflow clipping */}
          <div ref={moreMenuRef} className="relative hidden lg:block">
            <button
              onClick={() => setMoreMenuOpen(!moreMenuOpen)}
              className={cn(
                "flex items-center gap-1 px-4 py-2 text-sm font-medium",
                "text-muted-foreground hover:text-foreground transition-colors",
                moreMenuOpen && "text-foreground"
              )}
            >
              More
              <CaretDown
                className={cn(
                  "h-3 w-3 transition-transform duration-200",
                  moreMenuOpen && "rotate-180"
                )}
              />
            </button>

            {moreMenuOpen && (
              <div
                className={cn(
                  "absolute left-0 top-full mt-1 w-48 rounded-xl",
                  "bg-background border border-border shadow-xl",
                  "py-1 z-[100]",
                  "animate-flyout-enter"
                )}
              >
                <button
                  onClick={() => {
                    setMoreMenuOpen(false)
                    navigate("/directory")
                  }}
                  className="flex w-full items-center px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
                >
                  Staff Directory
                </button>


                <div className="border-t border-border my-1" />
                <div className="px-4 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Quick Links
                </div>
                <button
                  onClick={() => {
                    setMoreMenuOpen(false)
                    navigate("/matters")
                  }}
                  className="flex w-full items-center px-4 py-2 text-sm text-foreground hover:bg-muted transition-colors"
                >
                  All Matters
                </button>
                <button
                  onClick={() => {
                    setMoreMenuOpen(false)
                    navigate("/contacts")
                  }}
                  className="flex w-full items-center px-4 py-2 text-sm text-foreground hover:bg-muted transition-colors"
                >
                  All Contacts
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Right side */}
        <div className="flex items-center gap-1">
          {/* Mentions button */}
          <Link
            to="/mentions"
            className={cn(
              "hidden lg:flex items-center gap-2 h-7 px-2.5 rounded-md",
              "border border-border bg-muted",
              "text-xs text-muted-foreground font-medium",
              "transition-all duration-200",
              "hover:border-border hover:bg-accent hover:shadow-sm",
              "hover:text-foreground",
              "relative"
            )}
            title="View Mentions"
          >
            <At className="h-3.5 w-3.5" weight="bold" />
            <span>Mentions</span>
            {mentionsCount > 0 && (
              <span
                className={cn(
                  "absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1",
                  "rounded-full bg-destructive text-white",
                  "text-[10px] font-semibold",
                  "flex items-center justify-center"
                )}
              >
                {mentionsCount > 99 ? "99+" : mentionsCount}
              </span>
            )}
          </Link>

          {/* Chat button */}
          <button
            onClick={() => openChat()}
            className={cn(
              "hidden lg:flex items-center gap-2 h-7 px-2.5 rounded-md",
              "border border-border bg-muted",
              "text-xs text-muted-foreground font-medium",
              "transition-all duration-200",
              "hover:border-border hover:bg-accent hover:shadow-sm",
              "hover:text-foreground",
              "relative"
            )}
            title="Open Chat"
          >
            <ChatCircle className="h-3.5 w-3.5" />
            <span>Chat</span>
            {unreadCount > 0 && (
              <span
                className={cn(
                  "absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1",
                  "rounded-full bg-destructive text-white",
                  "text-[10px] font-semibold",
                  "flex items-center justify-center",
                  hasMentions && "animate-pulse"
                )}
              >
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </button>

          {/* Global AI trigger - hidden on mobile/tablet */}
          <button
            onClick={() => useGlobalAIStore.getState().setOpen(true)}
            className={cn(
              "hidden lg:flex items-center gap-2 h-7 px-2.5 rounded-md",
              "border border-border bg-muted",
              "text-xs text-muted-foreground font-medium",
              "transition-all duration-200",
              "hover:border-border hover:bg-accent hover:shadow-sm",
              "hover:text-foreground"
            )}
            title="Open Global AI"
          >
            <Robot className="h-3.5 w-3.5" weight="fill" />
            <span>BotBot</span>
          </button>
        </div>
      </div>

      {/* Global AI Drawer */}
      <GlobalAIDrawer />

      {/* Create modals */}
      <CreateContactModal
        open={createContactOpen}
        onOpenChange={setCreateContactOpen}
      />
      <CreateMatterModal
        open={createMatterOpen}
        onOpenChange={setCreateMatterOpen}
      />
      <SystemTicketDrawer
        open={ticketDrawerOpen}
        onOpenChange={setTicketDrawerOpen}
      />
    </div>
  )
}
